/**
 * @fileoverview Valor de uma opção da empresa que todos leem (GET /settings),
 * compartilhado entre as telas abertas (botão Resolver, quem vê as conversas).
 *
 * O último valor conhecido de cada empresa fica guardado e vale já no primeiro
 * desenho (abrir outra conversa não faz a tela piscar); passado o prazo, é
 * relido em segundo plano. O aviso por socket traz o valor novo, e todas as
 * telas abertas acompanham o mesmo valor: se um aviso se perder (socket caído),
 * a releitura de qualquer tela corrige as outras, e toda reconexão relê. Uma
 * leitura que volta depois de um aviso não o desfaz.
 */

import { useContext, useEffect, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const TTL_MS = 60 * 1000;
// opção -> { known, reading, subscribers }, cada um por empresa:
// known: { value, at, seq } (o último valor conhecido); reading: a leitura em
// andamento; subscribers: as telas abertas (o aviso de cada uma).
const stores = new Map();
// Ordem das gravações em `known` (o relógio empata no mesmo milissegundo).
let seq = 0;

const storeOf = (key) => {
  if (!stores.has(key)) stores.set(key, { known: new Map(), reading: new Map(), subscribers: new Map() });
  return stores.get(key);
};

const remember = (store, companyId, value) => {
  seq += 1;
  store.known.set(companyId, { value, at: Date.now(), seq });
  (store.subscribers.get(companyId) || []).forEach((notify) => notify());
};

/** Relê a opção quando o valor conhecido passou do prazo, ou sempre com force (uma leitura por vez). */
const refresh = (key, companyId, force = false) => {
  const store = storeOf(key);
  const hit = store.known.get(companyId);
  if (!force && hit && Date.now() - hit.at < TTL_MS) return Promise.resolve();
  if (store.reading.has(companyId)) return store.reading.get(companyId);
  const startSeq = seq;
  const request = api
    .get("/settings")
    .then(({ data }) => {
      const row = (Array.isArray(data) ? data : []).find((s) => s.key === key);
      // Um aviso por socket que chegou durante a leitura é mais novo que ela.
      if ((store.known.get(companyId)?.seq || 0) <= startSeq) remember(store, companyId, row?.value);
    })
    .catch(() => {
      // Falha momentânea: fica o que se sabia; a próxima tela tenta de novo.
    })
    .finally(() => store.reading.delete(companyId));
  store.reading.set(companyId, request);
  return request;
};

/**
 * [valor, situação] da opção na empresa do usuário logado. Situação: "known"
 * (lida ou avisada; sem a opção gravada, o valor é undefined), "loading" (nada
 * se sabe ainda) ou "failed" (a leitura falhou e nada se sabe). Com skip, não lê
 * nem acompanha (quem chama não depende da opção).
 */
const useCompanySetting = (key, { skip = false } = {}) => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  const read = () => {
    const hit = storeOf(key).known.get(companyId);
    return hit ? { value: hit.value, status: "known" } : { value: undefined, status: "loading" };
  };
  const [state, setState] = useState(read);

  useEffect(() => {
    if (!companyId || skip) return undefined;
    const store = storeOf(key);
    let active = true;
    const notify = () => {
      if (!active) return;
      const hit = store.known.get(companyId);
      setState(hit ? { value: hit.value, status: "known" } : { value: undefined, status: "loading" });
    };
    const listeners = store.subscribers.get(companyId) || new Set();
    listeners.add(notify);
    store.subscribers.set(companyId, listeners);
    notify();
    refresh(key, companyId).then(() => {
      if (active && !store.known.has(companyId)) setState({ value: undefined, status: "failed" });
    });

    const socket = socketManager.getSocket(companyId);
    const onSettings = (data) => {
      if (data?.action !== "update" || data?.setting?.key !== key) return;
      remember(store, companyId, data.setting.value);
    };
    // Reconectou (o servidor derruba a conexão quando o nível de quem vê as
    // conversas muda): um aviso pode ter se perdido no caminho.
    const onReady = () => refresh(key, companyId, true);
    socket.on(`company-${companyId}-settings`, onSettings);
    socket.on("ready", onReady);
    return () => {
      active = false;
      listeners.delete(notify);
      socket.off(`company-${companyId}-settings`, onSettings);
      socket.off("ready", onReady);
    };
  }, [socketManager, companyId, skip, key]);

  return [state.value, state.status];
};

export default useCompanySetting;
